import { useMemo } from 'react'
import * as React from 'react'
import gql from 'graphql-tag'
import update from 'immutability-helper'
import styled from 'styled-components'
import { useGqlNewsItemCommentListQuery } from 'generated/graphql'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import BounceLoading from 'components/BounceLoading'
import ExpandMoreIcon from 'svgIcons/ExpandMore'
import NewsMoreComment from 'svgIcons/NewsMoreComment'
import NewsItemComment from './NewsItemComment'

gql`
  query gqlNewsItemCommentList(
    $newsItemId: ID!
    $commentFirst: Int!
    $commentAfter: String
  ) {
    comments(
      first: $commentFirst
      after: $commentAfter
      newsItemId: $newsItemId
    ) {
      edges {
        ...NewsItemComment_NewsCommentEdge
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`

// todo first 개수 props로 넘겨줘야함.

export const DEFAULT_COMMENT_FIRST = 5
export const EXPANDED_COMMENT_FIRST = 10

type TProps = {
  newsItemId: string
  viewerIsAuthor: boolean
  expendComment?: boolean
  type?: 'card' | 'modal'
}

const NewsItemCommentList: React.FC<TProps> = ({
  type = 'card',
  newsItemId,
  viewerIsAuthor,
  expendComment = false,
}) => {
  const isMobile = useIsMobile()

  const { data, loading, fetchMore } = useGqlNewsItemCommentListQuery({
    variables: {
      newsItemId,
      commentFirst:
        isMobile || expendComment
          ? EXPANDED_COMMENT_FIRST
          : DEFAULT_COMMENT_FIRST,
      commentAfter: null,
    },
  })

  const handleMoreBtnClick = () => {
    if (!data) return
    const moreCommentsEdges = data.comments.edges
    const cursor = moreCommentsEdges[moreCommentsEdges.length - 1].cursor
    const variables = {
      newsItemId,
      commentFirst: EXPANDED_COMMENT_FIRST,
      commentAfter: cursor,
    }
    fetchMore({
      variables,
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousQueryResult
        return update(previousQueryResult, {
          comments: {
            edges: {
              $push: fetchMoreResult.comments.edges,
            },
            pageInfo: {
              $set: fetchMoreResult.comments.pageInfo,
            },
          },
        })
      },
    })
  }

  const hasMoreComment = useMemo(() => {
    if (!data) return false
    return data.comments.pageInfo.hasNextPage
  }, [data])

  return (
    <StyledWrapper>
      {/* data view */}
      {data && (
        <>
          {/* list */}
          {data.comments.edges.length > 0 && (
            <>
              {type === 'card' && <div className="hr"></div>}
              <div className="comment-list">
                {data.comments.edges.map((comment) => {
                  return (
                    <NewsItemComment
                      type={type}
                      newsItemId={newsItemId}
                      viewerIsNewsAuthor={viewerIsAuthor}
                      data={comment}
                      key={comment.cursor}
                    />
                  )
                })}
              </div>
            </>
          )}
          {/* empty */}
          {data.comments.edges.length === 0 && type === 'modal' && (
            <div className="empty-comment">
              <div className="empty-comment-title">아직 댓글이 없습니다.</div>
              <div className="empty-comment-desc">
                가장 먼저 댓글을 남겨보세요.
              </div>
            </div>
          )}
        </>
      )}
      {hasMoreComment && (
        <>
          {type === 'card' && (
            <a className="more-btn" onClick={handleMoreBtnClick}>
              댓글 더보기
              <ExpandMoreIcon className="expand-more-icon" />
            </a>
          )}
          {type === 'modal' && (
            <a className="modal-more-btn" onClick={handleMoreBtnClick}>
              <NewsMoreComment />
            </a>
          )}
        </>
      )}
      {loading && (
        <div className="loading-wrapper">
          <BounceLoading className="loading" />
        </div>
      )}
    </StyledWrapper>
  )
}

export default NewsItemCommentList

const StyledWrapper = styled.div`
  & > .hr {
    height: 1px;
    background-color: #e8e8e8;
    margin-bottom: 20px;
  }
  .comment-list {
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    margin-bottom: 20px;
  }
  .more-btn {
    display: flex;
    font-size: 14px;
    font-weight: 500;
    justify-content: center;
    padding-bottom: 24px;
    gap: 7px;
    .expand-more-icon {
      width: 13px;
    }
  }
  .modal-more-btn {
    position: relative;
    display: flex;
    justify-content: center;
    margin: 0 20px;
    svg {
      z-index: 1;
    }
    &::before {
      content: '';
      height: 1px;
      width: 100%;
      top: 50%;
      transform: translate(0, -3px);
      position: absolute;
      background-color: #eeeeee;
    }
  }
  .empty-comment {
    display: flex;
    flex-direction: column;
    color: #777;
    align-items: center;
    padding: 180px 0;
    .empty-comment-title {
      font-size: 15px;
    }
    .empty-comment-desc {
      font-size: 12px;
    }
  }
  .loading-wrapper {
    display: flex;
    justify-content: center;
    padding: 30px;
    .loading {
      width: fit-content;
      & > div {
        width: 8px;
        height: 8px;
      }
    }
  }
`
